<template>
  <div>
    <div class="flex items-center justify-between px-10 py-8 bg-gray-100 border-b rounded-t-lg">
      <div class="flex items-center gap-6">
        <div class="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
          <button
            class="px-4 py-1.5 font-medium"
            :class="mainTab === 'platform' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
            @click="mainTab = 'platform'"
          >
            Platform Activity
          </button>
          <button
            class="px-4 py-1.5 font-medium border-l border-gray-300"
            :class="mainTab === 'leads' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
            @click="mainTab = 'leads'"
          >
            Lead Activity
          </button>
        </div>
        <div
          v-if="mainTab === 'platform'"
          class="flex rounded-lg border border-gray-300 overflow-hidden text-sm"
        >
          <button
            class="px-4 py-1.5 font-medium"
            :class="activeTab === 'history' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
            @click="activeTab = 'history'"
          >
            History
          </button>
          <button
            class="px-4 py-1.5 font-medium border-l border-gray-300"
            :class="activeTab === 'scheduled' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
            @click="activeTab = 'scheduled'"
          >
            Scheduled
          </button>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <select
          v-model.number="pollIntervalMin"
          class="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option :value="1">
            1 min
          </option>
          <option :value="2">
            2 min
          </option>
          <option :value="5">
            5 min
          </option>
          <option :value="10">
            10 min
          </option>
          <option :value="15">
            15 min
          </option>
          <option :value="30">
            30 min
          </option>
          <option :value="60">
            60 min
          </option>
        </select>
        <button
          class="px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-50"
          @click="activeRef && activeRef.refresh()"
        >
          ↺ Refresh
        </button>
        <div class="w-px h-6 bg-gray-300" />
        <select
          v-if="mainTab === 'platform'"
          v-model="filterType"
          class="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option :value="null">
            All types
          </option>
          <option value="POST">
            Post
          </option>
          <option value="INVITATION">
            Invitation
          </option>
          <option value="MARKETING_TOPIC">
            Topic
          </option>
        </select>
        <select
          v-else
          v-model="leadFilterType"
          class="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option :value="null">
            All types
          </option>
          <option value="NEW_LEAD">
            New Lead
          </option>
          <option value="CONVERSION">
            Conversion
          </option>
        </select>
        <select
          v-model="filterPlatform"
          class="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          @change="clearCompanyFilter"
        >
          <option :value="null">
            All platforms
          </option>
          <option
            v-for="p in availablePlatforms"
            :key="p"
            :value="p"
          >
            {{ p.toUpperCase() }}
          </option>
        </select>
        <div class="flex items-stretch">
          <autocomplete
            :key="companyAutocompleteKey"
            class="company-autocomplete"
            :search="searchCompanies"
            :get-result-value="c => c.name"
            placeholder="All companies"
            @submit="selectCompany"
          />
          <button
            v-if="filterCompanyId"
            class="px-2 border border-l-0 border-gray-300 rounded-r bg-white text-gray-400 hover:text-gray-700 text-base leading-none"
            @click="clearCompanyFilter"
          >
            &times;
          </button>
        </div>
      </div>
    </div>
    <div class="w-full h-full">
      <ActivityFeed
        v-if="mainTab === 'platform'"
        ref="platformFeed"
        :filter-platform="filterPlatform"
        :filter-company-id="filterCompanyId"
        :filter-type="filterType"
        :scheduled="activeTab === 'scheduled'"
        :poll-interval-min="pollIntervalMin"
      />
      <LeadActivityFeed
        v-else
        ref="leadFeed"
        :filter-platform="filterPlatform"
        :filter-company-id="filterCompanyId"
        :filter-type="leadFilterType"
        :poll-interval-min="pollIntervalMin"
      />
    </div>
  </div>
</template>

<script>
import ActivityFeed from '@/components/post-history/ActivityFeed.vue'
import LeadActivityFeed from '@/components/lead-activity/LeadActivityFeed.vue'
import Autocomplete from '@trevoreyre/autocomplete-vue'
import '@trevoreyre/autocomplete-vue/dist/style.css'
import featureMixin from '@/mixins/feature'
import GET_COMPANIES_FOR_FILTER from '@/components/post-history/getCompaniesForFilter.gql'

export default {
  components: { ActivityFeed, LeadActivityFeed, Autocomplete },
  mixins: [featureMixin],
  data () {
    return {
      allCompanies: [],
      filterPlatform: null,
      filterCompanyId: null,
      filterType: null,
      leadFilterType: null,
      activeTab: 'history',
      mainTab: 'platform',
      pollIntervalMin: 5,
      companyAutocompleteKey: 0
    }
  },
  computed: {
    availablePlatforms () {
      return [...new Set(this.allCompanies.map(c => c.platform).filter(Boolean))].sort()
    },
    filteredCompanies () {
      if (!this.filterPlatform) return this.allCompanies
      return this.allCompanies.filter(c => c.platform === this.filterPlatform)
    },
    activeRef () {
      return this.mainTab === 'platform' ? this.$refs.platformFeed : this.$refs.leadFeed
    }
  },
  methods: {
    searchCompanies (query) {
      const q = query.toLowerCase()
      return this.filteredCompanies.filter(c => c.name.toLowerCase().includes(q))
    },
    selectCompany (company) {
      if (company) this.filterCompanyId = company.id
    },
    clearCompanyFilter () {
      this.filterCompanyId = null
      this.companyAutocompleteKey++
    }
  },
  apollo: {
    allCompanies: {
      query: GET_COMPANIES_FOR_FILTER,
      update: data => data.companies || []
    }
  }
}
</script>

<style>
.company-autocomplete .autocomplete-input {
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem 0 0 0.25rem;
  padding: 0.375rem 0.75rem;
  background-color: #fff;
  color: #374151;
  width: 180px;
  background-image: none !important;
  outline: none;
}
.company-autocomplete .autocomplete-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 1px #6366f1;
}
.company-autocomplete .autocomplete-result-list {
  z-index: 50;
  max-height: 260px;
  overflow-y: auto;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background: #fff;
}
.company-autocomplete .autocomplete-result {
  background-image: none !important;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
}
.company-autocomplete .autocomplete-result:hover,
.company-autocomplete .autocomplete-result[aria-selected="true"] {
  background-color: #e0e7ff;
}
</style>
